// The coach's device diagnostics (docs/design/AANTAMISVALMENTAJA.md; critic.md risk 1: "the iOS 26 mic and
// playback path is unknown"): what the microphone actually delivered, how far up its band reaches, how evenly
// the worklet's blocks arrive, what the person heard in the playback A/B, and whether the on-device model and
// the lips camera work on this phone. The app keeps what was measured since its last report on the device
// (app/src/coach/diagnostics.ts) and sends it with the next health report (app/src/health/report.ts → POST
// /api/health/report, field `coach`); the Worker cleans it again here and merges it into the device's kept one
// (mergeCoachDiagnostics; api/src/routes/health.ts), so a report with only the self-test keeps the mic's numbers.
//
// Privacy: numbers and fixed words only. The input's name is reduced to its kind on the phone (micKind):
// a device label can carry a person's name ("Axel's AirPods").
import type { MicReport } from './contract';

export type MicKind = 'builtin' | 'airpods' | 'bluetooth' | 'headset' | 'usb' | 'other' | 'unknown';
export const MIC_KINDS: readonly MicKind[] = ['builtin', 'airpods', 'bluetooth', 'headset', 'usb', 'other', 'unknown'];

/** The kind of an input from its label (track.label / MediaDeviceInfo.label). */
export function micKind(label: string | null | undefined): MicKind {
  const l = (label ?? '').toLowerCase();
  if (!l.trim()) return 'unknown';
  if (/airpods|beats/.test(l)) return 'airpods';
  if (/bluetooth|hands-?free|\bbt\b|\bhfp\b/.test(l)) return 'bluetooth';
  if (/usb/.test(l)) return 'usb';
  if (/headset|earpods|headphone|wired/.test(l)) return 'headset';
  if (/iphone|ipad|built-?in|internal|default|microphone array|mikrofoni|microphone/.test(l)) return 'builtin';
  return 'other';
}

export type PlaybackAB = 'same' | 'worse-with-mic' | 'unsure';
export const PLAYBACK_AB: readonly PlaybackAB[] = ['same', 'worse-with-mic', 'unsure'];

export interface CoachDiagnostics {
  /** The device's own random id (app/src/health/report.ts deviceId); the Worker keeps one report per device. */
  device: string | null;
  /** When the phone measured it (ISO). */
  at: string;
  /** What the input delivered; deviceLabel is a MicKind here, never the label itself. */
  mic: (Omit<MicReport, 'deviceLabel'> & { deviceLabel: MicKind }) | null;
  /** Mean power over speech of 4–8 kHz and 8.5–11 kHz, dB (a 16 kHz hands-free input has nothing in the upper band). */
  band: { lowDb: number; highDb: number; frames: number } | null;
  /** The input is band-limited (≤ 24 kHz rate, a hands-free kind, or no upper band measured): the sibilant map needs the phone's own mic. */
  bandLimited: boolean | null;
  context: { sampleRate: number; baseLatencyMs: number | null; outputLatencyMs: number | null } | null;
  /** Peterson's clip with the mic closed (A) and open (B): what the person heard (WebKit 311451 degrades playback while the mic is open). */
  playbackAB: PlaybackAB | null;
  model: {
    ok: boolean;
    reason: string | null;
    loadMs: number | null;
    runMs: number | null;
    argmaxAgree: number | null;
    tokensMatch: boolean | null;
    model: string;
  } | null;
  lips: { ok: boolean; reason: string | null; fps: number | null; detectMs: number | null; delegate: 'GPU' | 'CPU' | null } | null;
  /**
   * While the mic was open: interruptions (a call, Siri, the system, a context that would not start) and input
   * route changes. A report carries those since the phone's last report; the Worker adds them up (the device's
   * total since its first report; mergeCoachDiagnostics).
   */
  interruptions: number;
  routeChanges: number;
}

const num = (v: unknown, lo: number, hi: number): number | null => (typeof v === 'number' && Number.isFinite(v) ? Math.min(Math.max(v, lo), hi) : null);
const round = (v: number | null, digits = 1): number | null => (v === null ? null : Math.round(v * 10 ** digits) / 10 ** digits);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const word = (v: unknown, n = 24): string | null => (typeof v === 'string' && /^[a-z0-9._-]{1,40}$/i.test(v) ? v.slice(0, n) : null);
const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** A report as the Worker keeps it: bounded numbers and fixed words only; null when it is not one. */
export function cleanCoachDiagnostics(raw: unknown, now: number = Date.now()): CoachDiagnostics | null {
  const b = obj(raw);
  if (!b) return null;
  const t = typeof b.at === 'string' ? Date.parse(b.at) : NaN;
  const at = new Date(Number.isFinite(t) ? Math.min(Math.max(t, Date.UTC(2026, 0, 1)), now) : now).toISOString();
  const m = obj(b.mic);
  const mic: CoachDiagnostics['mic'] = m
    ? {
        sampleRate: num(m.sampleRate, 0, 384_000) ?? 0,
        channelCount: num(m.channelCount, 0, 32) ?? 0,
        echoCancellation: bool(m.echoCancellation),
        noiseSuppression: bool(m.noiseSuppression),
        autoGainControl: bool(m.autoGainControl),
        // the phone sends a kind; a raw label (an older app) is reduced here too
        deviceLabel: MIC_KINDS.includes(m.deviceLabel as MicKind)
          ? (m.deviceLabel as MicKind)
          : micKind(typeof m.deviceLabel === 'string' ? m.deviceLabel : ''),
        bandLimitHz: round(num(m.bandLimitHz, 0, 192_000), 0),
        hopP95Ms: round(num(m.hopP95Ms, 0, 10_000), 2),
        noiseFloorDb: round(num(m.noiseFloorDb, -200, 20)),
      }
    : null;
  const bd = obj(b.band);
  const band =
    bd && num(bd.lowDb, -200, 50) !== null && num(bd.highDb, -200, 50) !== null
      ? { lowDb: round(num(bd.lowDb, -200, 50))!, highDb: round(num(bd.highDb, -200, 50))!, frames: Math.round(num(bd.frames, 0, 1e7) ?? 0) }
      : null;
  const c = obj(b.context);
  const context =
    c && num(c.sampleRate, 1, 384_000) !== null
      ? {
          sampleRate: num(c.sampleRate, 1, 384_000)!,
          baseLatencyMs: round(num(c.baseLatencyMs, 0, 10_000), 2),
          outputLatencyMs: round(num(c.outputLatencyMs, 0, 10_000), 2),
        }
      : null;
  const md = obj(b.model);
  const model = md
    ? {
        ok: md.ok === true,
        reason: word(md.reason),
        loadMs: round(num(md.loadMs, 0, 600_000), 0),
        runMs: round(num(md.runMs, 0, 600_000), 0),
        argmaxAgree: round(num(md.argmaxAgree, 0, 1), 3),
        tokensMatch: bool(md.tokensMatch),
        model: word(md.model, 80) ?? '',
      }
    : null;
  const lp = obj(b.lips);
  const lips = lp
    ? {
        ok: lp.ok === true,
        reason: word(lp.reason),
        fps: round(num(lp.fps, 0, 240)),
        detectMs: round(num(lp.detectMs, 0, 10_000)),
        delegate: lp.delegate === 'GPU' ? ('GPU' as const) : lp.delegate === 'CPU' ? ('CPU' as const) : null,
      }
    : null;
  return {
    device: typeof b.device === 'string' && /^[A-Za-z0-9-]{8,40}$/.test(b.device) ? b.device : null,
    at,
    mic,
    band,
    bandLimited: bool(b.bandLimited),
    context,
    playbackAB: PLAYBACK_AB.includes(b.playbackAB as PlaybackAB) ? (b.playbackAB as PlaybackAB) : null,
    model,
    lips,
    interruptions: Math.round(num(b.interruptions, 0, 1e6) ?? 0),
    routeChanges: Math.round(num(b.routeChanges, 0, 1e6) ?? 0),
  };
}

const has = <T>(v: T | null | undefined): v is T => v !== null && v !== undefined;
const later = (a: string | undefined, b: string | undefined): string | undefined => {
  if (!a || !Number.isFinite(Date.parse(a))) return b ?? a;
  if (!b || !Number.isFinite(Date.parse(b))) return a;
  return Date.parse(b) >= Date.parse(a) ? b : a;
};

/**
 * A newer report over the one kept (the Worker's object per device, the phone's pending one). Each report carries
 * only what was measured since the phone's last report (a mic session, a self-test, the playback A/B, the lips
 * camera), so what it lacks (null or missing) keeps the older value, and the counters add up. The mic is the
 * latest input: a different input (another kind or rate) replaces the older mic and drops the older band
 * measurement (it was the other input's); the same input keeps the older measured numbers where the newer
 * session had none (closed before its first measurement).
 */
export function mergeCoachDiagnostics(prev: Partial<CoachDiagnostics> | null | undefined, next: Partial<CoachDiagnostics>): Partial<CoachDiagnostics> {
  const p = prev ?? {};
  const pick = <K extends 'context' | 'playbackAB' | 'model' | 'lips'>(k: K): CoachDiagnostics[K] | undefined => (has(next[k]) ? next[k] : p[k]);
  const out: Partial<CoachDiagnostics> = {
    ...(has(next.device) || has(p.device) ? { device: next.device ?? p.device } : {}),
    context: pick('context'),
    playbackAB: pick('playbackAB'),
    model: pick('model'),
    lips: pick('lips'),
    interruptions: (p.interruptions ?? 0) + (next.interruptions ?? 0),
    routeChanges: (p.routeChanges ?? 0) + (next.routeChanges ?? 0),
  };
  const at = later(p.at, next.at);
  if (at) out.at = at;
  const nm = next.mic;
  const pm = p.mic;
  if (!has(nm)) {
    out.mic = pm;
    out.band = has(next.band) ? next.band : p.band;
    out.bandLimited = has(next.bandLimited) ? next.bandLimited : p.bandLimited;
  } else if (!has(pm) || pm.deviceLabel !== nm.deviceLabel || pm.sampleRate !== nm.sampleRate) {
    out.mic = nm;
    out.band = next.band ?? null;
    out.bandLimited = next.bandLimited ?? null;
  } else {
    out.mic = { ...nm, bandLimitHz: nm.bandLimitHz ?? pm.bandLimitHz, hopP95Ms: nm.hopP95Ms ?? pm.hopP95Ms, noiseFloorDb: nm.noiseFloorDb ?? pm.noiseFloorDb };
    out.band = has(next.band) ? next.band : p.band;
    // the band flag goes with the band top it was judged from (rate and kind are the same input's)
    const keepFlag = nm.bandLimitHz === null && pm.bandLimitHz !== null && has(p.bandLimited);
    out.bandLimited = keepFlag ? p.bandLimited : has(next.bandLimited) ? next.bandLimited : p.bandLimited;
  }
  for (const k of Object.keys(out) as (keyof CoachDiagnostics)[]) if (out[k] === undefined) delete out[k];
  return out;
}

/** R2 key of a device's latest diagnostics (the bucket's diag/ prefix; never served to the phones). */
export const coachDiagKey = (userId: string, device: string): string => `diag/coach/${userId}/${device}.json`;
export const COACH_DIAG_PREFIX = 'diag/coach/';
